/** @module Camunda/ProcessActivity */
define([
  'dojo/_base/declare',
  'dojo/store/Memory',
  'underscore',
  'csi/def',
  'csi/locale',
  'csi/html',
  'csi/html/Widget',
  'csi/widget/Container',
  'csi/xml',
  './ProcessActivityItem',
  './main',
  'csi/store/Nodes',
  'dojo/text!./html/ProcessActivity.html'
], function (declare, Memory, _, def, locale, html, Widget, Container, xml, ProcessActivityItem, Camunda, Nodes, template) {
  const HIGHLIGHT_CSS_CLASS = 'csi-camunda-process-activity-highlight'

  return declare(Widget, {
    templateString: template,

    // attach points
    containerNode: null,

    /** @property {string} _highlightedElementId - task id of highlighted elements */
    _highlightedElementId: null,

    /**
     * @override
     * Populate view contents
     */
    _postViewDeferred: function (ctx, dfds) {
      dfds.push(this._createContainer(ctx))
    },

    /**
     * @override
     * Query for WbsElement and WBS which is not available in cache
     */
    onViewStart: function (dfds) {
      const { cacheView } = this
      const store = new Nodes({
        cachingStore: cacheView,
        typeNode: 'WbsElement',
        requestParameters: {
          'Dep.Path': 'Child:__Parent__'
        }
      })
      dfds.push(store.query())
      this.inherited(arguments)
    },

    /**
     * Create a container with process activity items
     * @param {Context} ctx
     */
    _createContainer: function (ctx) {
      const cache = this.cacheView
      const process = this.getViewNode()
      const tasks = cache.getNodes(process.Tasks)
      const c8Instance = cache.get(process.__Parent__)
      const processId = process.Id

      let data = []

      // add item if time is not zero
      function addItem (elementId, time, text, comment) {
        if (!time) {
          return
        }

        data.push({
          id: String(data.length + 1),
          elementId,
          text,
          comment,
        time})
      }

      // add activity items
      addItem(processId, process.StartDate, locale.resource('CamundaProcessStarted', [process.$Name]))

      for (const task of tasks) {
        const taskDefinition = cache.get(task.TaskDefinition)
        const assignee = cache.get(task.Assignee)
        const assigneeName = xml.nodeGetName(assignee) || ''
        const taskDefinitionId = taskDefinition.Id

        let assigneeLink = ''
        if (assigneeName) {
          const anchor = def.createBrowseAnchor(ctx, null, assignee)
          if (anchor) {
            html.text(anchor, assigneeName)
            assigneeLink = anchor.outerHTML
          } else {
            assigneeLink = assigneeName
          }
        }

        addItem(taskDefinitionId, task.StartDate, locale.resource('CamundaTaskStarted', [task.$Name]))
        if (assigneeName) {
          addItem(taskDefinitionId, task.AssignedDate, locale.resource('CamundaTaskAssigned', [task.$Name, assigneeLink]))
        }
        if (task.ActivityState == Camunda.STATE_SUSPENDED) {
          const processInstance = this.cacheView.get(task.__Parent__)
          const wbsElement = processInstance ? this.cacheView.get(processInstance.WorkflowExecutionHolder) : null
          const wbs = wbsElement ? this.cacheView.get(wbsElement.__Parent__) : null
          const wbsName = wbs ? wbs.$Name : ''
          addItem(taskDefinitionId, task.ModifiedAt, locale.resource('CamundaTaskSuspended', [task.$Name, wbsName]))
        } else if (task.ActivityState == Camunda.STATE_CANCELLED) {
          addItem(taskDefinitionId, task.EndDate, locale.resource('CamundaTaskCancelled', [task.$Name]))
        } else {
          addItem(taskDefinitionId, task.EndDate, locale.resource('CamundaTaskCompleted', [task.$Name, assigneeLink, task.UserResponse]), task.Comments)
        }
      }

      const wbsElement = this.cacheView.get(process.WorkflowExecutionHolder)
      const wbs = wbsElement ? this.cacheView.get(wbsElement.__Parent__) : null
      const wbsName = wbs ? wbs.$Name : ''

      switch (process.ActivityState) {
        case Camunda.STATE_SUSPENDED:
        case Camunda.STATE_STARTED:
          // Add items for suspended and started states only if wbsName is not empty
          if (wbsName !== '') {
            const resourceKey = process.ActivityState === Camunda.STATE_SUSPENDED
              ? 'CamundaProcessSuspended'
              : 'CamundaProcessRestarted'
            addItem(processId, process.ModifiedAt, locale.resource(resourceKey, [process.$Name, wbsName]))
          }
          break
        case Camunda.STATE_CANCELLED:
          addItem(processId, process.EndDate, locale.resource('CamundaProcessCancelled', [process.$Name]))
          break
        default:
          const instanceName = c8Instance ? c8Instance.$Name : ''
          addItem(processId, process.EndDate, locale.resource('CamundaProcessCompleted', [process.$Name, instanceName]))
          break
      }

      // sort activity items by time and present them
      data = _.sortBy(data, 'time')

      const store = new Memory({ data})

      const c = new Container({
        'class': 'csi-camunda-process-activity',
        store: store,
        widgetClass: ProcessActivityItem,
        widgetProperties: ProcessActivityItem.widgetProperties
      }).placeAt(this.containerNode)
      c.startup()
    },

    /**
     * Highlight process activity items belonging to this element
     * @param {string} elementId
     */
    onHoverElement: function (elementId) {
      this._highlightedElementId = elementId
      this.highlightElementItems(elementId, true)
    },

    /**
     * Remove highlighting of process activity items belonging to this element
     * @param {string} elementId
     */
    onOutElement: function (elementId) {
      if (this._highlightedElementId == elementId) {
        this.highlightElementItems(elementId, false)
      }
    },

    /**
     * Highlight (or remove highlighting) of items belonging this element
     *
     * @param {string} elementId
     * @param {boolean} highlight
     */
    highlightElementItems: function (elementId, highlight) {
      const nodeList = this.domNode.querySelectorAll('[' + ProcessActivityItem.ELEMENT_ID_ATTR_ID + '=' + elementId + ']')
      nodeList.forEach((node) => {
        node.classList.toggle(HIGHLIGHT_CSS_CLASS, highlight)
      })
    }
  })
})
